import type { ReactNode, JSX } from 'react';
import { useFeatureFlag, useFlagsReady } from '@hooks/feature-flags/useFeatureFlag';

interface FlagGateProps {
  /** LaunchDarkly flag key (camelCase, as `useFeatureFlag` expects). */
  flag: string;
  /** Value used until the LD client resolves — set to the LIVE value. */
  defaultValue?: boolean;
  children: ReactNode;
  /** Rendered when the flag is off (defaults to nothing). */
  fallback?: ReactNode;
  /**
   * Rendered while LaunchDarkly is still initializing, before the flag can be
   * trusted. Defaults to nothing. Pass a loader whenever `fallback` is a
   * redirect — otherwise a hard refresh of a deep link would navigate away on
   * the pre-init default before the real flag value streams in.
   */
  pending?: ReactNode;
}

/**
 * Renders `children` only when a feature flag resolves truthy. Composes with
 * `<PermissionGate>` and `<FeatureSlot>` to gate a route or nav entry on a
 * LaunchDarkly flag without each call site re-implementing the hook + branch.
 * The `defaultValue` should match what the flag resolves to on the live
 * deployment so the UI doesn't flicker before the LD client warms up.
 *
 * While LD is still initializing the gate renders `pending` (not `fallback`), so
 * a redirect fallback can't fire on the pre-init default during a cold load.
 */
export function FlagGate({
  flag,
  defaultValue = false,
  children,
  fallback = null,
  pending = null,
}: FlagGateProps): JSX.Element {
  const ready = useFlagsReady();
  const enabled = useFeatureFlag<boolean>(flag, defaultValue);
  if (!ready) return <>{pending}</>;
  return <>{enabled ? children : fallback}</>;
}
